import {
	EVINCED_SEVERITIES,
	EVINCED_SEVERITIES_IDS,
	ScreenshotBoundingBox
} from '@evinced-private/ui-common';

import { IElementInfo } from 'src/common/components/issue-element-info-formatter/IssueElementInfoFormatter';
import { Logger } from 'src/common/services/Logger';
import { TappableAreaMetadata } from 'src/common/types/MobileSingleSessionTypes';

// Generic type for mobile issue data
type MobileIssueData = {
	[key in ELEMENT_IDENTIFIER_KEY]: string[];
} & {
	elements__bounding_box_x?: number[];
	elements__bounding_box_y?: number[];
	elements__bounding_box_width?: number[];
	elements__bounding_box_height?: number[];
	tickets__key?: string[];
	tickets__link?: string[];
};

type IssueWithTaskManagerTickets = {
	tickets__key?: string[];
	tickets__link?: string[];
};

enum ELEMENT_IDENTIFIER_KEY {
	ACCESSIBILITY_LABEL = 'elements__accessibility_label',
	ACCESSIBILITY_IDENTIFIER = 'elements__accessibility_identifier',
	ELEMENT_TYPE = 'elements__element_type',
	CONTENT_DESCRIPTION = 'elements__content_description',
	VIEW_ID = 'elements__view_id',
	CLASS_NAME = 'elements__class_name'
}

export enum MOBILE_PLATFORM_IDS {
	ANDROID = 'ANDROID',
	IOS = 'IOS'
}

export enum MOBILE_PLATFORM {
	ANDROID = 'Android',
	IOS = 'iOS'
}

export enum ELEMENT_IDENTIFIER_TITLE {
	ACCESSIBILITY_LABEL = 'Accessible Name',
	ACCESSIBILITY_IDENTIFIER = 'Accessibility ID',
	ELEMENT_TYPE = 'Element Type',
	CONTENT_DESCRIPTION = 'Content Description',
	VIEW_ID = 'View ID',
	CLASS_NAME = 'Class Name'
}

export const MOBILE_ISSUE_SEVERITY_LIST: {
	key: EVINCED_SEVERITIES_IDS;
	name: EVINCED_SEVERITIES;
}[] = [
	{
		key: EVINCED_SEVERITIES_IDS.CRITICAL,
		name: EVINCED_SEVERITIES.CRITICAL
	},
	{
		key: EVINCED_SEVERITIES_IDS.SERIOUS,
		name: EVINCED_SEVERITIES.SERIOUS
	},
	{
		key: EVINCED_SEVERITIES_IDS.MODERATE,
		name: EVINCED_SEVERITIES.MODERATE
	},
	{
		key: EVINCED_SEVERITIES_IDS.MINOR,
		name: EVINCED_SEVERITIES.MINOR
	},
	{
		key: EVINCED_SEVERITIES_IDS.NEEDS_REVIEW,
		name: EVINCED_SEVERITIES.NEEDS_REVIEW
	},
	{
		key: EVINCED_SEVERITIES_IDS.BEST_PRACTICE,
		name: EVINCED_SEVERITIES.BEST_PRACTICE
	}
];

const ELEMENT_IDENTIFIERS_LIST: {
	key: ELEMENT_IDENTIFIER_KEY;
	title: ELEMENT_IDENTIFIER_TITLE;
}[] = [
	{
		key: ELEMENT_IDENTIFIER_KEY.ELEMENT_TYPE,
		title: ELEMENT_IDENTIFIER_TITLE.ELEMENT_TYPE
	},
	{
		key: ELEMENT_IDENTIFIER_KEY.ACCESSIBILITY_LABEL,
		title: ELEMENT_IDENTIFIER_TITLE.ACCESSIBILITY_LABEL
	},
	{
		key: ELEMENT_IDENTIFIER_KEY.CLASS_NAME,
		title: ELEMENT_IDENTIFIER_TITLE.CLASS_NAME
	},
	{
		key: ELEMENT_IDENTIFIER_KEY.VIEW_ID,
		title: ELEMENT_IDENTIFIER_TITLE.VIEW_ID
	},
	{
		key: ELEMENT_IDENTIFIER_KEY.ACCESSIBILITY_IDENTIFIER,
		title: ELEMENT_IDENTIFIER_TITLE.ACCESSIBILITY_IDENTIFIER
	},
	{
		key: ELEMENT_IDENTIFIER_KEY.CONTENT_DESCRIPTION,
		title: ELEMENT_IDENTIFIER_TITLE.CONTENT_DESCRIPTION
	}
];

export const MOBILE_SEVERITY_MAP: Record<EVINCED_SEVERITIES_IDS, EVINCED_SEVERITIES> = {
	[EVINCED_SEVERITIES_IDS.CRITICAL]: EVINCED_SEVERITIES.CRITICAL,
	[EVINCED_SEVERITIES_IDS.SERIOUS]: EVINCED_SEVERITIES.SERIOUS,
	[EVINCED_SEVERITIES_IDS.MODERATE]: EVINCED_SEVERITIES.MODERATE,
	[EVINCED_SEVERITIES_IDS.MINOR]: EVINCED_SEVERITIES.MINOR,
	[EVINCED_SEVERITIES_IDS.NEEDS_REVIEW]: EVINCED_SEVERITIES.NEEDS_REVIEW,
	[EVINCED_SEVERITIES_IDS.BEST_PRACTICE]: EVINCED_SEVERITIES.BEST_PRACTICE
};

export const buildElementInfo = (issue: MobileIssueData): IElementInfo[] => {
	const elementInfo: IElementInfo[] = [];
	ELEMENT_IDENTIFIERS_LIST.forEach(({ key, title }) => {
		const value = issue?.[key]?.join('');
		if (value) {
			elementInfo.push({ value, displayName: title });
		}
	});
	return elementInfo;
};

export const buildTaskManagerTickets = (
	issue: IssueWithTaskManagerTickets
): Array<{ key: string; link: string }> => {
	const taskManagerSystemTickets: Array<{ key: string; link: string }> = [];
	if (issue.tickets__key?.length && issue.tickets__link?.length) {
		for (let i = 0; i < issue.tickets__key.length; i++) {
			if (issue.tickets__key[i] && issue.tickets__link[i]) {
				taskManagerSystemTickets.push({
					key: issue.tickets__key[i],
					link: issue.tickets__link[i]
				});
			}
		}
	}
	return taskManagerSystemTickets;
};

export const buildBoundingBox = (issue: MobileIssueData): ScreenshotBoundingBox => {
	return {
		x: issue.elements__bounding_box_x?.[0] ?? 0,
		y: issue.elements__bounding_box_y?.[0] ?? 0,
		width: issue.elements__bounding_box_width?.[0] ?? 0,
		height: issue.elements__bounding_box_height?.[0] ?? 0
	};
};

const TAPPABLE_AREA_SIZE_KEYS = [
	'actualWidth',
	'actualHeight',
	'expectedWidth',
	'expectedHeight'
] as const;

/**
 * Parses the issue `metadata` JSON string and returns the tappable area size info,
 * or undefined when the metadata is missing, malformed or lacks size fields.
 */
export const buildTappableAreaMetadata = (
	metadata?: string | null
): TappableAreaMetadata | undefined => {
	if (!metadata) {
		return undefined;
	}

	let parsed: Record<string, unknown>;
	try {
		parsed = JSON.parse(metadata);
	} catch (error) {
		Logger.warn('Failed to parse mobile issue metadata', error);
		return undefined;
	}

	const hasAllSizes = TAPPABLE_AREA_SIZE_KEYS.every(
		(key) => typeof parsed?.[key] === 'number' && Number.isFinite(parsed[key])
	);
	if (!hasAllSizes) {
		return undefined;
	}

	return {
		actualWidth: parsed.actualWidth as number,
		actualHeight: parsed.actualHeight as number,
		expectedWidth: parsed.expectedWidth as number,
		expectedHeight: parsed.expectedHeight as number,
		elementSizeUnit: typeof parsed.elementSizeUnit === 'string' ? parsed.elementSizeUnit : ''
	};
};

const MOBILE_SDK_TYPES_IDS = {
	APPIUM_JAVA: 'APPIUM_JAVA',
	APPIUM_PYTHON: 'APPIUM_PYTHON',
	ESPRESSO: 'ESPRESSO',
	WDIO_MOBILE: 'WDIO_MOBILE',
	XCUI: 'XCUI'
} as const;

export type MobileSdkTypesIds = (typeof MOBILE_SDK_TYPES_IDS)[keyof typeof MOBILE_SDK_TYPES_IDS];

export const MOBILE_SDK_TYPES = {
	APPIUM_JAVA: 'Appium Java SDK',
	APPIUM_PYTHON: 'Appium Python SDK',
	ESPRESSO: 'Espresso SDK',
	WDIO_MOBILE: 'WebdriverIO SDK',
	XCUI: 'XCUI SDK'
} as const;

export type MobileSdkTypes = (typeof MOBILE_SDK_TYPES)[keyof typeof MOBILE_SDK_TYPES];

export const MOBILE_SDK_TYPES_MAP: Record<MobileSdkTypesIds, MobileSdkTypes> = {
	[MOBILE_SDK_TYPES_IDS.APPIUM_JAVA]: MOBILE_SDK_TYPES.APPIUM_JAVA,
	[MOBILE_SDK_TYPES_IDS.APPIUM_PYTHON]: MOBILE_SDK_TYPES.APPIUM_PYTHON,
	[MOBILE_SDK_TYPES_IDS.ESPRESSO]: MOBILE_SDK_TYPES.ESPRESSO,
	[MOBILE_SDK_TYPES_IDS.WDIO_MOBILE]: MOBILE_SDK_TYPES.WDIO_MOBILE,
	[MOBILE_SDK_TYPES_IDS.XCUI]: MOBILE_SDK_TYPES.XCUI
};
